import cx from 'classnames';
import {map} from 'lodash';
import {useCallback, Fragment} from 'react';
import {useTranslation} from 'react-i18next';

import {Accordion, AccordionDetails, AccordionShortDetails, AccordionTitle, CopyText, Icon, DataWrapper} from 'components';
import {SessionAppointment} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';

import {AppointmentsRendererProps} from '../types';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.APPOINTMENTS.TABLE;

type Props = AppointmentsRendererProps;

export const AppointmentsAccordion: React.FC<Props> = ({
    loading, appointments, renderAssignmentInfo, renderCreateInfo, renderScheduledDates, renderParticipants, renderActions,
    getSourceName, onOpenConnectInfoModal
}: Props) => {
    const {t} = useTranslation();

    const renderShortInfo = useCallback((session: SessionAppointment) => (
        <Fragment>
            {(session.schedule?.startDate || session.schedule?.endDate) && (
                <div className='tw-flex tw-ml-6 tw-mb-2'>
                    <Icon name='calendar' color='default-dimmed' />
                    <div className='tw-ml-2'>{renderScheduledDates(session.schedule)}</div>
                </div>
            )}
            {session.assignment?.groupId && (
                <div className='tw-flex tw-ml-6 tw-mb-2'>
                    <Icon name='assignment' color='default-dimmed' />
                    <div className='tw-ml-2'>{renderAssignmentInfo(session.assignment)}</div>
                </div>
            )}
            {renderParticipants(session, 'tw-ml-6 tw-mb-2')}
        </Fragment>
    ), [renderAssignmentInfo, renderScheduledDates, renderParticipants]);

    return (
        <DataWrapper isLoading={loading} noData={!appointments.length}>
            {map(appointments, ({session, links}) => (
                <Accordion
                    key={session.id}
                    className='tw-mb-2 lg:tw-hidden'
                >
                    <AccordionTitle>
                        <div className='tw-flex tw-items-center tw-justify-between'>
                            <div className='tw-flex-1'>
                                {session.name && <div className='tw-ts-base-bold'>{session.name}</div>}
                                <div className={cx('tw-flex tw-items-center', session.name ? 'tw-text-sm' : 'tw-ts-base-bold')}>
                                    <Icon name='id' className='tw-mr-1' />
                                    <CopyText
                                        iconColor='default-dimmed'
                                        text={session.id}
                                        iconSize='sm'
                                        labelPlacement='left'
                                    />
                                </div>
                            </div>
                            {renderActions(session)}
                        </div>
                    </AccordionTitle>
                    <AccordionShortDetails>
                        <div className='sm:tw-grid tw-grid-cols-3 tw-text-break-word'>
                            {renderShortInfo(session)}
                        </div>
                    </AccordionShortDetails>
                    <AccordionDetails>
                        <div className='sm:tw-grid tw-grid-cols-3 tw-text-break-word'>
                            {renderShortInfo(session)}
                            <div className='tw-flex tw-items-center tw-ml-6 tw-mb-2'>
                                <Icon name='info' color='default-dimmed' />
                                <div>
                                    <div className='tw-flex tw-flex-wrap tw-ml-2'>
                                        <span
                                            className='tw-text-primary tw-cursor-pointer'
                                            onClick={() => onOpenConnectInfoModal({links, session})}
                                        >
                                            {t(T_KEYS.COLUMNS.CONNECT)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className='tw-flex tw-ml-6 tw-mb-2'>
                                <Icon name='clock' color='default-dimmed' />
                                <div className='tw-ml-2'>{renderCreateInfo(session.createdAt, getSourceName(session))}</div>
                            </div>
                        </div>
                    </AccordionDetails>
                </Accordion>
            ))}
        </DataWrapper>
    );
};
